import { useCallback, useEffect, useState } from "react";
import { useDisclosure, Spinner } from "@chakra-ui/react";

import type {
	QuoteTemplate,
	SaveQuoteTemplatePayload,
} from "../../../api/quote-templates-api";
import { createQuoteSettingsImportApi } from "../../../api/quote-templates-import-api";
import { useUndoStack } from "../hooks/useUndoStack";
import { useQuoteTemplates } from "./hooks/useQuoteTemplates";
import { dehydrateDraftRowForSave, useTemplateDraft } from "./hooks/useTemplateDraft";
import { TemplateList } from "./TemplateList";
import { TemplateEditor } from "./TemplateEditor";
import { DeleteTemplateDialog } from "./DeleteTemplateDialog";
import { AiSpreadsheetImport } from "../AiSpreadsheetImport";
import { BLANK_TEMPLATE_VALUE, type DraftSnapshot } from "./types";

export interface QuoteTemplateSettingsProps {
	companyId?: number;
	/**
	 * URL-driven editing id. When set, the component opens that template for
	 * editing once templates have loaded. When null/undefined, the list view
	 * is shown (unless `isCreatingNew` is true).
	 */
	editingId?: number | null;
	/** URL-driven "new template" flag. */
	isCreatingNew?: boolean;
	/**
	 * Notifies the parent when the user opens, closes, or creates a template
	 * so the parent can keep the URL in sync. Optional — when omitted the
	 * component falls back to fully-internal state.
	 */
	onEditingChange?: (id: number | null, opts?: { isNew?: boolean }) => void;
}

/**
 * Orchestrator for the Quote Templates settings page.
 *
 * Responsibilities (everything else is delegated):
 *   - top-level mode: list vs editor (`editingTemplate`/`isCreating`)
 *   - URL ⇄ state sync (when `onEditingChange` is supplied)
 *   - undo stack wiring (snapshot from `useTemplateDraft`, restore via draft API)
 *   - save / cancel / delete orchestration across `useQuoteTemplates` + draft
 *   - validation banner (`validationError`)
 *
 * UI is rendered by `<TemplateList>` and `<TemplateEditor>`; the delete
 * confirmation lives in `<DeleteTemplateDialog>`.
 */
const QuoteTemplateSettings: React.FC<QuoteTemplateSettingsProps> = ({
	editingId = null,
	isCreatingNew = false,
	onEditingChange,
}) => {
	const {
		templates,
		isLoading,
		isSaving,
		isDeleting,
		saveTemplate,
		removeTemplate,
		fetchTemplates,
	} = useQuoteTemplates();

	const draft = useTemplateDraft();
	const {
		clearDraft,
		loadTemplate,
		cloneTemplate,
		restoreSnapshot,
		currentSnapshot,
		draftName,
		draftDescription,
		draftQuoteTableName,
		draftRows,
		rowsMissingUnit,
		columnManager,
	} = draft;

	const [editingTemplate, setEditingTemplate] = useState<QuoteTemplate | null>(
		null
	);
	const [isCreating, setIsCreating] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const {
		isOpen: isImportOpen,
		onOpen: onImportOpen,
		onClose: onImportClose,
	} = useDisclosure();
	// Settings-surface import has no rfxId/rfx_template_id to key off, so the
	// shared preview modal is mounted with an explicit `importApi` override
	// rather than a `resourceType`-resolved endpoint (see quote-templates-import-api.ts).
	const quoteSettingsImportApi = createQuoteSettingsImportApi();

	const isEditorOpen = isCreating || editingTemplate !== null;

	// -----------------------------------------------------------------------
	// Undo stack (capacity 5, debounced 400 ms)
	// -----------------------------------------------------------------------
	const { canUndo, undo, reset: resetUndo, suppressNext } = useUndoStack<DraftSnapshot>({
		current: currentSnapshot,
		enabled: isEditorOpen,
	});

	const handleUndo = useCallback(() => {
		const snapshot = undo();
		if (!snapshot) return;
		// Restoring fires the snapshot effect again; suppressNext stops the
		// restored snapshot itself from being recorded.
		suppressNext();
		restoreSnapshot(snapshot);
	}, [undo, suppressNext, restoreSnapshot]);

	// -----------------------------------------------------------------------
	// Editor lifecycle
	// -----------------------------------------------------------------------
	const resetAll = useCallback(() => {
		clearDraft();
		setEditingTemplate(null);
		setIsCreating(false);
		setValidationError(null);
		resetUndo();
	}, [clearDraft, resetUndo]);

	const handleStartCreate = useCallback(() => {
		setIsCreating(true);
		setEditingTemplate(null);
		setValidationError(null);
		clearDraft();
		resetUndo();
	}, [clearDraft, resetUndo]);

	const handleStartEdit = useCallback(
		(template: QuoteTemplate) => {
			setEditingTemplate(template);
			setIsCreating(false);
			setValidationError(null);
			loadTemplate(template);
			resetUndo();
		},
		[loadTemplate, resetUndo]
	);

	// -----------------------------------------------------------------------
	// URL → state sync (opt-in via `onEditingChange`)
	// -----------------------------------------------------------------------
	useEffect(() => {
		if (!onEditingChange) return;

		if (isCreatingNew) {
			if (!isCreating) handleStartCreate();
			return;
		}

		if (editingId == null) {
			if (isCreating || editingTemplate !== null) resetAll();
			return;
		}

		if (editingTemplate?.id === editingId) return;

		const target = templates.find((t) => t.id === editingId);
		if (target) handleStartEdit(target);
		// Re-runs when templates load.
	}, [
		onEditingChange,
		editingId,
		isCreatingNew,
		templates,
		editingTemplate,
		isCreating,
		handleStartCreate,
		handleStartEdit,
		resetAll,
	]);

	// "Start from" picker (creation only)
	const handleStartFromTemplate = useCallback(
		(templateId: string | number) => {
			if (templateId === BLANK_TEMPLATE_VALUE || templateId === "") {
				clearDraft();
				resetUndo();
				return;
			}
			const numeric =
				typeof templateId === "number" ? templateId : Number(templateId);
			const template = templates.find((t) => t.id === numeric);
			if (!template) return;
			cloneTemplate(template, " (copy)");
			resetUndo();
		},
		[templates, cloneTemplate, clearDraft, resetUndo]
	);

	// Clear the validation banner the moment the user fixes all violations.
	useEffect(() => {
		if (validationError && rowsMissingUnit.size === 0 && draftName.trim()) {
			setValidationError(null);
		}
	}, [validationError, rowsMissingUnit, draftName]);

	// Cmd/Ctrl-Z keybinding while editor is open.
	useEffect(() => {
		if (!isEditorOpen) return;
		const handler = (e: KeyboardEvent) => {
			const isUndo =
				(e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "z";
			if (!isUndo) return;
			const target = e.target as HTMLElement | null;
			const tag = target?.tagName?.toLowerCase();
			if (tag === "input" || tag === "textarea" || tag === "select") return;
			e.preventDefault();
			handleUndo();
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [isEditorOpen, handleUndo]);

	// -----------------------------------------------------------------------
	// Save / delete
	// -----------------------------------------------------------------------
	const handleSave = useCallback(async () => {
		if (!draftName.trim()) {
			setValidationError("Template name is required.");
			return;
		}
		if (rowsMissingUnit.size > 0) {
			setValidationError(
				`Unit of Measurement is required for every row when the column is used. ${rowsMissingUnit.size} row(s) are missing a unit.`
			);
			return;
		}
		setValidationError(null);

		const payload: SaveQuoteTemplatePayload = {
			name: draftName.trim(),
			description: draftDescription.trim() ? draftDescription.trim() : null,
			quote_table_name: draftQuoteTableName.trim()
				? draftQuoteTableName.trim()
				: null,
			columns: columnManager.columns.map((c) => ({
				column_type: c.columnType,
				label: c.label,
				sort_order: c.sortOrder,
			})),
			rows: draftRows.map((r) =>
				dehydrateDraftRowForSave(r, columnManager.columns)
			),
		};

		const ok = await saveTemplate(payload, editingTemplate?.id);
		if (ok) {
			resetAll();
			onEditingChange?.(null);
		}
	}, [
		draftName,
		draftDescription,
		draftQuoteTableName,
		columnManager.columns,
		draftRows,
		rowsMissingUnit,
		editingTemplate,
		saveTemplate,
		resetAll,
		onEditingChange,
	]);

	const handleCancel = useCallback(() => {
		resetAll();
		onEditingChange?.(null);
	}, [resetAll, onEditingChange]);

	const handleDeleteConfirm = useCallback(
		(templateId: number) => {
			setDeleteTargetId(templateId);
			onOpen();
		},
		[onOpen]
	);

	const handleDelete = useCallback(async () => {
		if (deleteTargetId === null) return;
		const wasEditingTarget = editingTemplate?.id === deleteTargetId;
		const ok = await removeTemplate(deleteTargetId);
		if (ok && wasEditingTarget) {
			resetAll();
			onEditingChange?.(null);
		}
		setDeleteTargetId(null);
		onClose();
	}, [
		deleteTargetId,
		editingTemplate,
		removeTemplate,
		resetAll,
		onClose,
		onEditingChange,
	]);

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------
	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-12"
				data-testid="templates-loading"
			>
				<Spinner size="lg" />
			</div>
		);
	}

	return (
		<div data-testid="quote-template-settings">
			{!isEditorOpen && (
				<TemplateList
					templates={templates}
					onCreate={() => {
						if (onEditingChange) {
							onEditingChange(null, { isNew: true });
						} else {
							handleStartCreate();
						}
					}}
					onEdit={(template) => {
						if (onEditingChange) {
							onEditingChange(template.id);
						} else {
							handleStartEdit(template);
						}
					}}
					onDelete={handleDeleteConfirm}
					onImport={onImportOpen}
				/>
			)}

			{isImportOpen && (
				<AiSpreadsheetImport
					isOpen={isImportOpen}
					resourceId={0}
					resourceType="quote-settings"
					importApi={quoteSettingsImportApi}
					onClose={onImportClose}
					onImportComplete={fetchTemplates}
				/>
			)}

			{isEditorOpen && (
				<TemplateEditor
					draft={draft}
					isCreating={isCreating}
					isEditing={editingTemplate !== null}
					isSaving={isSaving}
					canUndo={canUndo}
					validationError={validationError}
					templates={templates}
					onUndo={handleUndo}
					onCancel={handleCancel}
					onSave={handleSave}
					onStartFromTemplate={handleStartFromTemplate}
				/>
			)}

			<DeleteTemplateDialog
				isOpen={isOpen}
				isDeleting={isDeleting}
				onCancel={onClose}
				onConfirm={handleDelete}
			/>
		</div>
	);
};

export { QuoteTemplateSettings };
